/**
 * App.tsx (refactored)
 *
 * Changes vs original:
 * 1. React.lazy + Suspense for ALL route components → code splitting
 *    (initial bundle was loading all 25+ containers eagerly)
 * 2. Fixed memory leak: 3 overlapping setInterval calls reduced to 1
 * 3. Extracted follow-up polling into a custom hook (useFollowUpReminder)
 * 4. Replaced `any` types with proper ones
 * 5. Global alert effect cleaned up (was missing messageApi in deps)
 */
import React, { lazy, Suspense, useEffect, useRef, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import { message, Spin } from 'antd';
import { setError, setFollowUpData } from './store/reducers';
import type { RootState } from './types';
import './styles.scss';
import SalesService from './services/Sales.services';

// ─── Lazy-loaded route components ────────────────────────────
// Each becomes its own JS chunk — only loaded when the route is visited.
const Auth               = lazy(() => import('./containers/Auth'));
const Layout             = lazy(() => import('./containers/Layout'));
const Dashboard          = lazy(() => import('./containers/Dashboard'));
const Departments        = lazy(() => import('./containers/Departments'));
const DepartmentForm     = lazy(() => import('./containers/Departments/Form'));
const Admin              = lazy(() => import('./containers/Admin'));
const AdminForm          = lazy(() => import('./containers/Admin/Form'));
const Team               = lazy(() => import('./containers/Team'));
const TeamForm           = lazy(() => import('./containers/Team/Form'));
const TeamLead           = lazy(() => import('./containers/TeamLead'));
const TeamLeadForm       = lazy(() => import('./containers/TeamLead/Form'));
const Reports            = lazy(() => import('./containers/Reports'));
const Search             = lazy(() => import('./containers/Reports/search'));
const Sales              = lazy(() => import('./containers/Sales'));
const SalesData          = lazy(() => import('./containers/Sales/Form'));
const SalesDataReports   = lazy(() => import('./containers/Sales/Sales-Report'));
const Leads              = lazy(() => import('./containers/Leads'));
const Lead               = lazy(() => import('./containers/Leads/Lead'));
const DocumentsStorageView = lazy(() => import('./containers/Leads/DocumentsStorageView'));
const Referals           = lazy(() => import('./containers/Referals'));
const Notifications      = lazy(() => import('./containers/Notifications'));
const PushMessages       = lazy(() => import('./containers/PushMessages'));
const Configure          = lazy(() => import('./containers/Configure'));
const TaxConfiguration   = lazy(() => import('./containers/TaxConfiguration'));
const Support            = lazy(() => import('./containers/Support'));
const SupportComments    = lazy(() => import('./containers/Support/Form/Comments'));
const Escalation         = lazy(() => import('./containers/Escalation'));
const EscalationComments = lazy(() => import('./containers/Escalation/Form/Comments'));
const ProtectedRoutes    = lazy(() => import('./components/ProtectedRoutes'));
const UnAuthorized       = lazy(() => import('./components/UnAuthorized'));
const NotFound           = lazy(() => import('./components/NotFound'));
const GlobalFollowUpReminder = lazy(
  () => import('./containers/Sales/Sales-Report/GlobalFollowUpReminder'),
);

// ─── Full-page spinner for lazy boundaries ────────────────────
const PageSpinner = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: 'var(--clr-bg)',
    }}
  >
    <Spin size="large" />
  </div>
);

// ─── Route config (unchanged shape) ──────────────────────────
const routerComponents = [
  { path: '/dashboard',                          Component: Dashboard },
  { path: '/departments',                        Component: Departments },
  { path: '/departments/create',                 Component: DepartmentForm },
  { path: '/departments/edit/:id',               Component: DepartmentForm },
  { path: '/admin',                              Component: Admin },
  { path: '/admin/create',                       Component: AdminForm },
  { path: '/admin/edit/:id',                     Component: AdminForm },
  { path: '/teamlead',                           Component: TeamLead },
  { path: '/teamlead/create',                    Component: TeamLeadForm },
  { path: '/teamlead/edit/:id',                  Component: TeamLeadForm },
  { path: '/team',                               Component: Team },
  { path: '/team/create',                        Component: TeamForm },
  { path: '/team/edit/:id',                      Component: TeamForm },
  { path: '/reports',                            Component: Reports },
  { path: '/search',                             Component: Search },
  { path: '/configuration',                      Component: Configure },
  { path: '/sales',                              Component: Sales },
  { path: '/sales/data-upload',                  Component: SalesData },
  { path: '/sales/report',                       Component: SalesDataReports },
  { path: '/sales/followup-calls',               Component: SalesDataReports },
  { path: '/leads',                              Component: () => <Leads isLeads /> },
  { path: '/walkouts',                           Component: () => <Leads isLeads={false} /> },
  { path: '/leads/create',                       Component: Lead },
  { path: '/leads/edit/:id',                     Component: Lead },
  { path: '/leads/edit/:id/documents-storage',   Component: DocumentsStorageView },
  { path: '/referals',                           Component: Referals },
  { path: '/notifications',                      Component: Notifications },
  { path: '/pushmessages',                       Component: PushMessages },
  {
    path: '/tax-configuration',
    Component: () => (
      <TaxConfiguration section="TAX_CONFIG" leadData={null} showHeader />
    ),
  },
  { path: '/support',                            Component: Support },
  { path: '/support/view/:id',                   Component: SupportComments },
  { path: '/escalation',                         Component: Escalation },
  { path: '/escalation/view/:id',                Component: EscalationComments },
];

// ─── Follow-up polling hook ───────────────────────────────────
// FIX: The original App.tsx had 3 overlapping setInterval calls (lines 120,
// 168, 193) that were all independently re-creating themselves on each
// `reminders` state change, causing exponential interval accumulation.
// This hook uses a single ref-based interval cleared properly on unmount.

const POLL_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

function useFollowUpReminder(
  isLoggedIn: boolean,
  onShowReminder: (show: boolean) => void,
) {
  const dispatch = useDispatch();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchFollowups = useCallback(async () => {
    try {
      const response = await SalesService.getsalesList(
        '/sales/follow-ups?notification=true',
      );
      const { totalRows = 0, data = [] } = response ?? {};

      onShowReminder(totalRows > 0);

      // Check if any follow-up is within the next 30 minutes
      const hasUpcoming = (data as Array<Record<string, string>>).some((item) => {
        if (!item.follow_up_date || !item.curdatetime) return false;
        const diff =
          new Date(item.follow_up_date).getTime() -
          new Date(item.curdatetime).getTime();
        return diff >= 0 && diff <= 30 * 60 * 1000;
      });
      dispatch(setFollowUpData(hasUpcoming));
    } catch (err) {
      // Non-critical — silently ignore
    }
  }, [dispatch, onShowReminder]);

  useEffect(() => {
    if (!isLoggedIn) return;

    // Fire immediately on mount, then every POLL_INTERVAL_MS
    fetchFollowups();
    intervalRef.current = setInterval(fetchFollowups, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isLoggedIn, fetchFollowups]);
}

// ─── App ──────────────────────────────────────────────────────
function App() {
  const [messageApi, contextHolder] = message.useMessage();
  const dispatch = useDispatch();

  const { showAlert, alertMessage, alertType } = useSelector(
    (state: RootState) => state.store,
  );

  const [showReminder, setShowReminder] = React.useState(false);
  const isLoggedIn = Boolean(localStorage.getItem('user_details'));
  const userRole = JSON.parse(localStorage.getItem('user_details') || '{}')?.role;

  // Only poll follow-ups for role === '3' (team members)
  useFollowUpReminder(isLoggedIn && userRole === '3', setShowReminder);

  // Global alert toaster
  useEffect(() => {
    if (!showAlert) return;
    messageApi.open({ type: alertType, content: alertMessage });
    const t = setTimeout(
      () => dispatch(setError({ status: false, type: undefined, message: '' })),
      1200,
    );
    return () => clearTimeout(t);
  }, [showAlert, alertType, alertMessage, dispatch, messageApi]);

  return (
    <div className="App">
      <ToastContainer />
      {contextHolder}

      <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/" element={<Auth />} />
          <Route path="/unauthorized" element={<UnAuthorized />} />

          <Route element={<Layout />}>
            {routerComponents.map(({ path, Component }) => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoutes>
                    <Component />
                  </ProtectedRoutes>
                }
              />
            ))}
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      <GlobalFollowUpReminder
        visible={showReminder}
        onClose={() => setShowReminder(false)}
      />
    </div>
  );
}

export default App;
